/**
 * Annotation Types
 *
 * TypeScript types for transcript annotations and summaries.
 * Matches the backend schemas for annotation API responses.
 */

/**
 * Type of annotation on a transcript segment
 */
export type AnnotationType =
  | "pattern"
  | "defense"
  | "criticism"
  | "blame"
  | "valid_feeling"
  | "positive"
  | "insight";

/**
 * Severity level of an annotation
 */
export type AnnotationSeverity = "info" | "mild" | "moderate" | "significant";

/**
 * How the annotation was generated
 */
export type AnnotationGenerationType = "automatic" | "detailed";

/**
 * Minimal therapy style info for annotations
 */
export interface TherapyStyleSummary {
  id: string;
  slug: string;
  name: string;
}

/**
 * Full segment annotation response
 */
export interface SegmentAnnotationResponse {
  id: string;
  transcript_id: string;
  segment_id: string | null;
  therapy_style: TherapyStyleSummary | null;
  annotation_type: AnnotationType;
  severity: AnnotationSeverity;
  title: string;
  description: string;
  references_segment_ids: string[] | null;
  block_label: string | null;
  generation_type: AnnotationGenerationType;
  generated_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * Minimal annotation for embedding in segment responses
 */
export interface SegmentAnnotationSummary {
  id: string;
  annotation_type: AnnotationType;
  severity: AnnotationSeverity;
  title: string;
}

/**
 * Annotation summary for a transcript
 */
export interface AnnotationSummaryResponse {
  id: string;
  transcript_id: string;
  therapy_style: TherapyStyleSummary | null;
  summary_text: string;
  pattern_counts: Record<string, number> | null;
  speaker_patterns: Record<string, Record<string, number>> | null;
  generated_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * Response for listing annotations with summary
 */
export interface AnnotationListResponse {
  annotations: SegmentAnnotationResponse[];
  summary: AnnotationSummaryResponse | null;
  total_count: number;
  pattern_counts: Record<string, number> | null;
}

/**
 * Request for generating annotations
 */
export interface GenerateAnnotationsRequest {
  therapy_style_id?: string;
  regenerate?: boolean;
}

/**
 * Response for annotation generation request
 */
export interface GenerateAnnotationsResponse {
  status: "generating" | "completed";
  message: string;
  annotation_count: number | null;
}

/**
 * Annotations grouped by segment
 */
export interface AnnotationsBySegmentResponse {
  segment_id: string;
  sequence_order: number;
  speaker_label: string;
  annotations: SegmentAnnotationSummary[];
}

/**
 * Annotations grouped by type
 */
export interface AnnotationsByTypeResponse {
  annotation_type: AnnotationType;
  count: number;
  annotations: SegmentAnnotationResponse[];
}

/**
 * Color mapping for annotation types
 * Uses green for positive/strength annotations and red for negative/growth areas
 * to match the InsightsSidebar color scheme for visual consistency
 */
export const ANNOTATION_TYPE_COLORS: Record<
  AnnotationType,
  { bg: string; text: string; border: string }
> = {
  // Strengths - green
  pattern: {
    bg: "bg-green-50 dark:bg-green-950",
    text: "text-green-700 dark:text-green-300",
    border: "border-green-200 dark:border-green-800",
  },
  valid_feeling: {
    bg: "bg-green-50 dark:bg-green-950",
    text: "text-green-700 dark:text-green-300",
    border: "border-green-200 dark:border-green-800",
  },
  positive: {
    bg: "bg-green-50 dark:bg-green-950",
    text: "text-green-700 dark:text-green-300",
    border: "border-green-200 dark:border-green-800",
  },
  insight: {
    bg: "bg-green-50 dark:bg-green-950",
    text: "text-green-700 dark:text-green-300",
    border: "border-green-200 dark:border-green-800",
  },
  // Growth areas - red
  defense: {
    bg: "bg-red-50 dark:bg-red-950",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800",
  },
  criticism: {
    bg: "bg-red-50 dark:bg-red-950",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800",
  },
  blame: {
    bg: "bg-red-50 dark:bg-red-950",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800",
  },
};

/**
 * Color mapping for severity levels
 */
export const SEVERITY_COLORS: Record<
  AnnotationSeverity,
  { bg: string; text: string }
> = {
  info: { bg: "bg-muted", text: "text-muted-foreground" },
  mild: {
    bg: "bg-yellow-100 dark:bg-yellow-900",
    text: "text-yellow-700 dark:text-yellow-300",
  },
  moderate: {
    bg: "bg-orange-100 dark:bg-orange-900",
    text: "text-orange-700 dark:text-orange-300",
  },
  significant: {
    bg: "bg-red-100 dark:bg-red-900",
    text: "text-red-700 dark:text-red-300",
  },
};

/**
 * Human-readable labels for annotation types
 */
export const ANNOTATION_TYPE_LABELS: Record<AnnotationType, string> = {
  pattern: "Pattern",
  defense: "Defensiveness",
  criticism: "Criticism",
  blame: "Blame",
  valid_feeling: "Valid Feeling",
  positive: "Positive",
  insight: "Insight",
};
